import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { BRAND_NAME } from "@/lib/brand";

import {
  CONTACT_EMAIL,
  ENTITY,
  JURISDICTION,
  LegalBullets,
  LegalEmphasis,
  LegalH2,
  LegalP,
  LegalShell,
} from "./_shared";

export function Terms() {
  const { t } = useTranslation();

  return (
    <LegalShell title={t("legal.terms.title")} lastUpdated="2026-09-25">
      <LegalP>
        These Terms of Service ("Terms") govern your access to and use of the {BRAND_NAME} web and
        mobile applications and any related services (the "Service") operated by {ENTITY} ("
        {BRAND_NAME}", "we", "us"). The Service provides AI-assisted equity research, market
        intelligence, portfolio monitoring, strategy building and backtesting, and optional
        brokerage-linked trading features. By creating an account or using the Service, you agree to
        be bound by these Terms and by our{" "}
        <Link to="/legal/privacy" className="text-primary underline">
          {t("legal.privacy.title")}
        </Link>
        . If you do not agree, do not use the Service.
      </LegalP>

      <LegalEmphasis>
        {BRAND_NAME} provides research tools, not investment advice. Nothing you see in the Service
        is a recommendation to buy, sell, or hold any security, and nothing is an offer or
        solicitation to transact in any security. You are solely responsible for your own investment
        decisions. If you enable trading features, orders are executed by your own brokerage under
        your authority. See Sections 7 through 9 below for the full statements.
      </LegalEmphasis>

      <LegalH2>1. Eligibility</LegalH2>
      <LegalP>
        You must be at least 18 years old (or the age of majority in your jurisdiction, whichever is
        higher) and capable of forming a binding contract to use the Service. Your account is
        personal to you: do not share your credentials or transfer your account to anyone else. You
        must not use the Service if you are barred from doing so under applicable laws (for example,
        sanctions or securities regulations).
      </LegalP>

      <LegalH2>2. Restricted jurisdictions and sanctions</LegalH2>
      <LegalP>
        The Service is not available in, and must not be accessed from, any country or region
        subject to comprehensive sanctions administered by the U.S. Office of Foreign Assets Control
        (OFAC), the United Nations, the European Union, or the United Kingdom, and it must not be
        used by any person or entity named on a government sanctions or denied-party list. By using
        the Service you represent that you are not such a person and are not located in such a
        territory. Using a VPN or any other means to disguise your location in order to circumvent
        these restrictions is a material breach of these Terms.
      </LegalP>

      <LegalH2>3. Accounts</LegalH2>
      <LegalBullets
        items={[
          "You are responsible for the accuracy of the information you provide when registering and for keeping your credentials confidential.",
          `You are responsible for all activity that occurs under your account. Notify us at ${CONTACT_EMAIL} immediately if you suspect unauthorized access.`,
          "You may delete your account at any time from Settings → Security & Privacy. We may suspend or terminate accounts that violate these Terms (see Section 12).",
        ]}
      />

      <LegalH2>4. Acceptable use</LegalH2>
      <LegalP>You agree not to:</LegalP>
      <LegalBullets
        items={[
          "Scrape, mirror, or systematically copy the Service or any data presented through it for redistribution.",
          "Resell, sublicense, or commercially exploit the Service, in whole or in part, without our prior written consent.",
          "Reverse-engineer, decompile, or attempt to extract the source code of the Service, except where applicable law expressly permits it.",
          "Access the Service by automated means except through the interfaces we officially provide and document — the web and mobile applications and, where your plan includes it, our documented MCP endpoint. Search-engine indexing of public pages is permitted when clearly identified and rate-limited.",
          "Use the Service or its outputs to train, fine-tune, or benchmark a machine-learning model, or to build a competing product or dataset, without our prior written consent.",
          "Use the Service — including any strategy or agent feature — to engage in market manipulation of any kind, such as wash trading, spoofing, layering, or front-running.",
          "Use the Service to send spam, malware, unlawful content, or to harass other users.",
          "Attempt to gain unauthorized access to the Service, to other users' accounts, or to any underlying system, network, or data.",
          "Use the Service in any way that violates applicable law, including securities, anti-fraud, sanctions, anti-money-laundering, or data-protection law.",
        ]}
      />

      <LegalH2>5. Third-party data and content</LegalH2>
      <LegalP>
        The Service displays financial data, filings, news, market quotes, and other information
        sourced from third parties (including, without limitation, Financial Modeling Prep, the U.S.
        Securities and Exchange Commission's EDGAR system, and market-data and news providers). That
        data is provided "as is" by those third parties and is subject to their own terms and
        licenses. We do not guarantee that third-party data is accurate, complete, current, or
        available, and we do not endorse any third-party content. Use of third-party data is at your
        own risk.
      </LegalP>

      <LegalH2>6. AI-generated content</LegalH2>
      <LegalP>
        The Service includes AI features that generate text, analysis, and suggestions in response
        to your prompts and the loaded data. AI-generated output can be incomplete, out of date, or
        factually incorrect, and may misinterpret the underlying data. You are solely responsible
        for independently evaluating any AI output before acting on it. Treat it as commentary, not
        as fact, and verify anything you intend to rely on against primary sources. We make no
        warranty as to the accuracy, completeness, reliability, or suitability of any AI-generated
        output, and we disclaim liability for decisions you make on the basis of it.
      </LegalP>

      <LegalH2>7. No professional advice</LegalH2>
      <LegalP>
        The Service is provided for informational and educational purposes only. Everything it
        produces — including reports, screeners, signals, scores, strategy suggestions, backtest
        results, and price levels — is informational and does not constitute investment advice,
        financial planning, tax advice, legal advice, accounting advice, or any other professional
        advice. We are not registered as a broker-dealer, investment adviser, or commodity trading
        advisor with the SEC, FINRA, the CFTC, or any other regulator, and no fiduciary or advisory
        relationship is created by your use of the Service. Consult a licensed professional before
        making any investment decision.
      </LegalP>

      <LegalH2>8. Investment and trading risk</LegalH2>
      <LegalBullets
        items={[
          "Trading and investing in securities involves substantial risk. You may lose some or all of the capital you invest. Past performance does not predict future results.",
          "Backtests, simulations, paper-trading results, and projections are hypothetical. They assume idealized conditions and may not account for slippage, fees, liquidity, gaps, or market impact; live results can differ materially.",
          "Stop-loss and other protective order levels are not guaranteed. In fast or illiquid markets, orders can fill far from the intended price or not fill at all.",
          "Options and other derivatives carry additional risks, including rapid loss of the entire premium, assignment, and expiration. Thinly traded instruments carry heightened volatility and liquidity risk.",
        ]}
      />

      <LegalH2>9. Brokerage connections and automated trading</LegalH2>
      <LegalP>
        Trading features are optional and available only if you connect a brokerage account. When
        you do, orders placed through the Service are routed to and executed by your brokerage. Your
        funds and securities are held by your broker, and your brokerage relationship is solely
        between you and that broker. {BRAND_NAME} is not a broker-dealer and never takes custody of
        your funds or securities; connections use the broker's own authorization flow, and we never
        receive your brokerage password.
      </LegalP>
      <LegalBullets
        items={[
          "Orders the agent proposes require your approval before submission. A strategy you explicitly deploy for live trading may place orders automatically within that strategy's rules. In both cases orders are placed under your authority, and you are responsible for them.",
          "Automated strategies can behave unexpectedly. Software defects, stale or incorrect data, or abnormal market conditions can produce unintended orders and losses. Monitor any strategy you keep running.",
          "Pausing or stopping a strategy is not instantaneous. Orders already sent to your broker may execute before a stop takes effect, and executed trades cannot be reversed.",
          "Broker outages, order rejections, and execution delays are outside our control, and we are not responsible for them.",
          "Paper trading is simulated. Simulated fills and results will differ from live trading.",
        ]}
      />

      <LegalH2>10. Fees, subscriptions, and usage credits</LegalH2>
      <LegalBullets
        items={[
          "Paid plans are billed in advance through our payment processor at the price and terms shown at purchase. We may change plan pricing or included features with advance notice; changes take effect at your next billing period.",
          "Each plan includes a usage allowance for AI features that resets every billing period. Unused allowance does not roll over and has no cash value.",
          "You may purchase additional usage credits. Purchased credits are consumed by usage, are non-transferable, and have no cash value.",
          "Except where required by law or expressly stated otherwise, fees are non-refundable once a billing period has begun, and consumed usage is non-refundable.",
          "If you initiate a payment dispute for usage that has already been consumed, we may reverse the associated credits and suspend the account while the dispute is investigated.",
        ]}
      />

      <LegalH2>11. Intellectual property</LegalH2>
      <LegalP>
        {BRAND_NAME}, the {BRAND_NAME} name and logos, the underlying software, the visual design,
        and the structure and presentation of the reports are owned by {ENTITY} and protected by
        intellectual-property laws. We grant you a personal, limited, non-exclusive,
        non-transferable, revocable license to access and use the Service in accordance with these
        Terms. No other rights are granted.
      </LegalP>
      <LegalP>
        You retain ownership of the inputs you submit to the Service (for example, the prompts you
        send to the AI assistant). You grant us a worldwide, royalty-free license to process those
        inputs solely as necessary to operate and improve the Service.
      </LegalP>

      <LegalH2>12. Suspension and termination</LegalH2>
      <LegalP>
        We may suspend or terminate your access to the Service at any time, with or without notice,
        if we reasonably believe you have violated these Terms or that continued access poses a risk
        to other users, to us, or to a third party. You may stop using the Service at any time.
      </LegalP>
      <LegalP>
        On termination, active strategies are deactivated, pending agent-proposed orders are
        cancelled where possible, and brokerage connections are disconnected. Positions in your
        brokerage account remain yours and are unaffected — manage them directly with your broker.
        If we terminate for a material breach, any remaining usage allowance and purchased credits
        are forfeited to the extent permitted by law; if we discontinue the Service or terminate
        your account without cause, we will refund the unused portion of any purchased credits.
        Provisions that by their nature should survive termination — including ownership,
        disclaimers, limitation of liability, indemnification, and dispute resolution — will
        continue to apply.
      </LegalP>

      <LegalH2 id="disclaimers">13. Disclaimers</LegalH2>
      <LegalP>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
        EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, COMPLETENESS, TIMELINESS, AND NON-INFRINGEMENT.
        WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY
        DATA DISPLAYED THROUGH IT IS ACCURATE OR CURRENT. SOME JURISDICTIONS DO NOT ALLOW THE
        EXCLUSION OF CERTAIN WARRANTIES, IN WHICH CASE THE ABOVE EXCLUSIONS MAY NOT APPLY TO YOU.
      </LegalP>

      <LegalH2>14. Limitation of liability</LegalH2>
      <LegalP>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER {BRAND_NAME.toUpperCase()} NOR ITS
        AFFILIATES, NOR ANY OF OUR DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR LICENSORS, WILL BE
        LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES,
        OR FOR ANY LOSS OF PROFITS, REVENUES, DATA, OR GOODWILL, OR FOR ANY TRADING OR INVESTMENT
        LOSSES, OR FOR THE ACTS, OMISSIONS, OR FAILURES OF ANY BROKERAGE, EXECUTION VENUE, OR DATA
        PROVIDER, ARISING OUT OF OR RELATED TO YOUR USE OF, OR INABILITY TO USE, THE SERVICE — EVEN
        IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL AGGREGATE LIABILITY
        FOR ALL CLAIMS RELATED TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
        US, IF ANY, IN THE TWELVE MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE
        HUNDRED U.S. DOLLARS. MANDATORY CONSUMER RIGHTS THAT CANNOT BE CONTRACTUALLY WAIVED ARE NOT
        AFFECTED.
      </LegalP>

      <LegalH2>15. Indemnification</LegalH2>
      <LegalP>
        You agree to defend, indemnify, and hold harmless {ENTITY} and its affiliates from and
        against any third-party claims, damages, liabilities, costs, and expenses (including
        reasonable attorneys' fees) arising out of (a) your use or misuse of the Service, (b) your
        violation of these Terms, or (c) your violation of any applicable law or the rights of a
        third party.
      </LegalP>

      <LegalH2>16. Governing law and disputes</LegalH2>
      <LegalP>
        These Terms are governed by the laws of {JURISDICTION}, without regard to its
        conflict-of-laws principles. The exclusive venue for any dispute arising out of or related
        to these Terms or the Service will be the state or federal courts located in that
        jurisdiction, and you consent to the personal jurisdiction of those courts. Nothing in this
        section limits any non-waivable rights you may have as a consumer under the laws of your
        home country.
      </LegalP>

      <LegalH2>17. Changes to these Terms</LegalH2>
      <LegalP>
        We may update these Terms from time to time. When we do, we will update the "Last updated"
        date above and, for material changes, surface a notice in-app or by email. Your continued
        use of the Service after a change takes effect constitutes acceptance of the updated Terms.
      </LegalP>

      <LegalH2>18. Contact</LegalH2>
      <LegalP>Questions about these Terms can be sent to {CONTACT_EMAIL}.</LegalP>
    </LegalShell>
  );
}
